import {
  deleteAws360Files,
  deleteAwsFile,
  downloadAwsFile,
  getAwsContentLinks,
  getExplorer3dUnits,
  getExplorer3dUnitTypes,
  listAwsFilesFolders,
  postAwsDragDrop,
  uploadAwsFiles,
  uploadAwsVideos,
} from '~/services/api';

export const FLOOR_PLAN_SUBSECTION_TABS = [
  { id: 'plans', label: 'Plans' },
  { id: 'images', label: 'Photos' },
  { id: 'videos', label: 'Videos' },
  { id: '360s', label: '360 scans' },
];

export const UNIT_SUBSECTION_TABS = [
  { id: 'images', label: 'Images' },
  { id: 'balconies', label: 'Exterior views' },
  { id: 'videos', label: 'Videos' },
  { id: '360s', label: '360 scans' },
];

export function pickNonEmptyString(value) {
  if (value == null) return '';
  return String(value).trim();
}

export function normalizeExplorerContentList(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && Array.isArray(data.list)) {
    return data.list;
  }
  return [];
}

export async function loadExplorerContentLists(explorer3dId) {
  const [unitsRes, typesRes] = await Promise.all([
    getExplorer3dUnits(explorer3dId),
    getExplorer3dUnitTypes(explorer3dId),
  ]);

  return {
    units: normalizeExplorerContentList(unitsRes),
    unitTypes: normalizeExplorerContentList(typesRes),
  };
}

export function getFloorPlanListLabel(row) {
  const raw = row && row.name;
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  if (raw != null && String(raw).trim()) return String(raw).trim();
  return 'No name';
}

export function getContentUnitListLabel(row) {
  const seenicUnitId = pickNonEmptyString(row && row.seenicUnitId);
  if (seenicUnitId) return seenicUnitId;
  const num = pickNonEmptyString(row && row.number);
  if (num) return num;
  const display = pickNonEmptyString(row && row.displayName);
  if (display) return display;
  return pickNonEmptyString(row && row.id);
}

export function matchesContentSearch(label, rawQuery) {
  const query = String(rawQuery || '')
    .trim()
    .toLowerCase();
  if (!query) return true;
  return String(label || '')
    .toLowerCase()
    .includes(query);
}

export function defaultSubsectionForTab(tab) {
  return tab === 'units' ? 'images' : 'plans';
}

function unwrapUnknown(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
  if (data.result != null) return unwrapUnknown(data.result);
  return data;
}

function parseJsonIfString(value) {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  try {
    return JSON.parse(trimmed);
  } catch (e) {
    return value;
  }
}

export function normalizeAwsUrlsResponse(data) {
  const value = parseJsonIfString(unwrapUnknown(data));
  return Array.isArray(value) ? value : [];
}

export async function loadAwsUrls(entityId, contentType) {
  if (!pickNonEmptyString(entityId)) return [];
  const data = await getAwsContentLinks(String(entityId).trim(), contentType);
  return normalizeAwsUrlsResponse(data);
}

export function getExplorerContentAwsBucketName() {
  const fromEnv = process.env.EXPLORER_CONTENT_AWS_BUCKET;
  if (typeof fromEnv === 'string' && fromEnv.trim()) return fromEnv.trim();
  return '3dexplorer-content';
}

export function buildExplorerContentAwsFolderName(
  explorer3dId,
  entityType,
  entityId,
  contentType,
) {
  return `locations/${explorer3dId}/${entityType}/${entityId}/content/${contentType}`;
}

export function getAwsFileAccept(contentType) {
  if (contentType === 'videos') return 'video/*';
  if (contentType === 'images' || contentType === 'balconies') {
    return 'image/*';
  }
  return 'image/*,.svg,.pdf';
}

export function galleryMediaExt(fileName, url) {
  if (fileName && fileName.includes('.')) {
    return fileName.split('.').pop().toLowerCase();
  }
  const path = String(url || '').split('?')[0];
  const seg = path.split('/').pop() || '';
  return seg.includes('.') ? seg.split('.').pop().toLowerCase() : '';
}

export function isAwsGalleryVideoExt(ext) {
  return ['mp4', 'webm', 'mov', 'm4v'].includes(ext);
}

export function getAwsContentUrlSortableId(item) {
  const link = pickNonEmptyString(item && item.link);
  if (link) return link;
  const fileName = pickNonEmptyString(item && item.fileName);
  const lastModified =
    item && item.lastModified != null ? String(item.lastModified) : '';
  return `aws-item:${fileName}:${lastModified}`;
}

export function buildAwsContentUrlSortableIds(items) {
  const counts = new Map();
  return items.map(item => {
    const base = getAwsContentUrlSortableId(item);
    const next = (counts.get(base) || 0) + 1;
    counts.set(base, next);
    return next === 1 ? base : `${base}::__${next}`;
  });
}

export async function downloadAwsContentFile(fileUrl, filename) {
  const blob = await downloadAwsFile(fileUrl);
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename && filename.trim() ? filename : 'download';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
}

export async function uploadExplorerContentFiles({
  files,
  explorer3dId,
  entityType,
  entityId,
  contentType,
  updatedBy,
}) {
  const modifiedFiles = files.map(
    file =>
      new File([file], file.name.replace(/\s+/g, '_'), { type: file.type }),
  );
  const bucketName = getExplorerContentAwsBucketName();
  const folderName = buildExplorerContentAwsFolderName(
    explorer3dId,
    entityType,
    entityId,
    contentType,
  );

  if (contentType === 'videos') {
    await uploadAwsVideos(modifiedFiles, bucketName, folderName, updatedBy);
  } else {
    await uploadAwsFiles(modifiedFiles, bucketName, folderName, updatedBy);
  }

  return loadAwsUrls(entityId, contentType);
}

export async function deleteExplorerContentFile(
  fileUrl,
  entityType,
  updatedBy,
) {
  const data = await deleteAwsFile(fileUrl, entityType, updatedBy);
  return {
    message: data && data.message,
    urls: normalizeAwsUrlsResponse(data && data.urls),
  };
}

export async function saveAwsContentUrlsOrder(params) {
  await postAwsDragDrop(params);
}

function ensureTrailingSlash(value) {
  return value.endsWith('/') ? value : `${value}/`;
}

function s3KeyFromPublicUrl(url) {
  const raw = String(url || '')
    .split('?')[0]
    .trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw);
    return decodeURIComponent(parsed.pathname.replace(/^\/+/, ''));
  } catch (e) {
    return decodeURIComponent(
      raw.replace(/^https?:\/\/[^/]+\//, '').replace(/^\/+/, ''),
    );
  }
}

function lastPathSegment(value) {
  const cleaned = String(value || '').replace(/\/+$/, '');
  const parts = cleaned.split('/');
  return parts[parts.length - 1] || cleaned;
}

const IMAGE_FILE_EXT_RE = /\.(jpe?g|png|webp|gif|bmp|avif)$/i;

function isImageFileName(name) {
  return IMAGE_FILE_EXT_RE.test(name);
}

function isPreviewRelativePath(relativePath) {
  const lower = String(relativePath || '')
    .toLowerCase()
    .replace(/\\/g, '/');
  const parts = lower.split('/').filter(Boolean);
  if (parts.some(part => part === 'previews' || part === 'preview'))
    return true;
  const base = parts[parts.length - 1] || '';
  return /preview/i.test(base);
}

function compareRelativePaths(a, b) {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

function pickFirstPreviewLink(files) {
  const images = files.filter(
    file => isImageFileName(file.fileName) || isImageFileName(file.path),
  );
  const previews = images.filter(file => isPreviewRelativePath(file.path));
  const pool = (previews.length > 0 ? previews : images)
    .slice()
    .sort((a, b) => compareRelativePaths(a.path, b.path));
  return (pool[0] && pool[0].link) || '';
}

function isLikelyAwsFolderItem(item) {
  const link = pickNonEmptyString(item && item.link);
  const name = pickNonEmptyString(item && item.fileName);
  if (!link && !name) return false;
  if (link.endsWith('/')) return true;
  if (name && !name.includes('.')) return true;
  const key = s3KeyFromPublicUrl(link);
  const last = lastPathSegment(key);
  return Boolean(last) && !last.includes('.');
}

function folderUrlFromFileLink(fileLink, folderName) {
  const marker = folderName ? `/content/360s/${folderName}` : '/content/360s';
  const idx = fileLink.indexOf(marker);
  if (idx >= 0)
    return ensureTrailingSlash(fileLink.slice(0, idx + marker.length));
  const key = s3KeyFromPublicUrl(fileLink);
  const prefix = folderName
    ? key.slice(
        0,
        Math.max(0, key.indexOf(`/${folderName}/`) + folderName.length + 1),
      )
    : key.replace(/\/[^/]+$/, '');
  if (!prefix) return ensureTrailingSlash(fileLink.replace(/\/[^/]+$/, ''));
  try {
    const parsed = new URL(fileLink);
    return `${parsed.origin}/${ensureTrailingSlash(prefix)}`;
  } catch (e) {
    return ensureTrailingSlash(fileLink.replace(/\/[^/]+$/, ''));
  }
}

function zipPathForListedFile(relativePath, folderName) {
  const rel = String(relativePath || '')
    .replace(/^\/+/, '')
    .replace(/\\/g, '/');
  if (!folderName) {
    const base = rel.split('/').pop();
    return base && base.trim() ? base : rel;
  }
  const prefix = `${folderName}/`;
  if (rel.startsWith(prefix)) return rel.slice(prefix.length);
  return rel;
}

function normalizeListedAwsObjects(data) {
  const value = parseJsonIfString(unwrapUnknown(data));
  if (!Array.isArray(value)) return [];
  return value.flatMap(item => {
    if (!item || typeof item !== 'object') return [];
    const relativePath = pickNonEmptyString(item.fileName);
    const link = pickNonEmptyString(item.link);
    if (!relativePath && !link) return [];
    return [
      {
        fileName:
          lastPathSegment(relativePath) ||
          lastPathSegment(s3KeyFromPublicUrl(link)),
        link: link || undefined,
        lastModified:
          typeof item.lastModified === 'string' ||
          typeof item.lastModified === 'number'
            ? item.lastModified
            : undefined,
        isFolder: item.isFolder === true,
        relativePath: relativePath || lastPathSegment(s3KeyFromPublicUrl(link)),
      },
    ];
  });
}

async function listAwsPrefixObjects(prefix) {
  const normalized = ensureTrailingSlash(prefix.replace(/^\/+/, ''));
  const data = await listAwsFilesFolders(normalized);
  return normalizeListedAwsObjects(data);
}

function filesFromListedObjects(objects, folderName) {
  return objects
    .filter(item => !item.isFolder && pickNonEmptyString(item.link))
    .map(item => ({
      link: pickNonEmptyString(item.link),
      path: zipPathForListedFile(item.relativePath, folderName),
      fileName: item.fileName || lastPathSegment(item.relativePath),
    }))
    .filter(item => item.path && item.link);
}

function buildScanFolder(folderName, folderUrl, objects) {
  const files = filesFromListedObjects(objects, folderName);
  return {
    folderName: folderName || '360 scan',
    folderUrl: ensureTrailingSlash(folderUrl),
    previewUrl: pickFirstPreviewLink(files),
    files,
  };
}

export async function resolveExplorer360ScanFolders({
  explorer3dId,
  entityId,
  entityType,
  storedItems,
}) {
  const rootPrefix = `locations/${explorer3dId}/${entityType}/${entityId}/content/360s/`;
  let listed = [];
  try {
    listed = await listAwsPrefixObjects(rootPrefix);
  } catch (error) {
    console.error('Failed to list 360 scan files', error);
  }

  const groups = new Map();
  listed.forEach(item => {
    if (item.isFolder || !pickNonEmptyString(item.link)) return;
    const rel = item.relativePath.replace(/^\/+/, '');
    const slash = rel.indexOf('/');
    const folderName = slash === -1 ? '' : rel.slice(0, slash);
    const current = groups.get(folderName) || [];
    current.push(item);
    groups.set(folderName, current);
  });

  const storedFolders = (storedItems || []).filter(
    item => isLikelyAwsFolderItem(item) && pickNonEmptyString(item.link),
  );

  if (storedFolders.length > 0) {
    const resolved = [];
    for (let i = 0; i < storedFolders.length; i += 1) {
      const item = storedFolders[i];
      const folderUrl = ensureTrailingSlash(pickNonEmptyString(item.link));
      const folderName =
        pickNonEmptyString(item.fileName) ||
        lastPathSegment(s3KeyFromPublicUrl(folderUrl));
      let objects = groups.get(folderName) || [];
      if (objects.length === 0 && folderUrl) {
        try {
          objects = await listAwsPrefixObjects(s3KeyFromPublicUrl(folderUrl));
        } catch (error) {
          console.error('Failed to list 360 scan folder', folderUrl, error);
        }
      }
      const sampleLink = objects[0] && objects[0].link;
      resolved.push(
        buildScanFolder(
          folderName,
          folderUrl || folderUrlFromFileLink(sampleLink || '', folderName),
          objects,
        ),
      );
    }
    return resolved;
  }

  if (groups.size === 0) return [];

  return Array.from(groups.entries()).map(([folderName, objects]) => {
    const sampleLink = pickNonEmptyString(objects[0] && objects[0].link);
    const folderUrl = sampleLink
      ? folderUrlFromFileLink(sampleLink, folderName)
      : `locations/${explorer3dId}/${entityType}/${entityId}/content/360s/${folderName}/`;
    return buildScanFolder(folderName, folderUrl, objects);
  });
}

export async function deleteExplorer360ScanFolder(folderUrl, updatedBy) {
  const data = await deleteAws360Files(folderUrl, updatedBy);
  return normalizeAwsUrlsResponse(data && data.urls);
}

/* eslint-disable no-bitwise */
const CRC32_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) {
      c = (c & 1) !== 0 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[i] = c >>> 0;
  }
  return table;
})();

function crc32(bytes) {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = CRC32_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function writeUint16(target, offset, value) {
  target[offset] = value & 0xff;
  target[offset + 1] = (value >>> 8) & 0xff;
}

function writeUint32(target, offset, value) {
  target[offset] = value & 0xff;
  target[offset + 1] = (value >>> 8) & 0xff;
  target[offset + 2] = (value >>> 16) & 0xff;
  target[offset + 3] = (value >>> 24) & 0xff;
}
/* eslint-enable no-bitwise */

function sanitizeZipPath(path) {
  return path
    .replace(/\\/g, '/')
    .replace(/^\/+/, '')
    .replace(/\/{2,}/g, '/')
    .split('/')
    .filter(part => part !== '' && part !== '.' && part !== '..')
    .join('/');
}

function buildStoreZip(files) {
  const encoder = new TextEncoder();
  const localParts = [];
  const centralParts = [];
  let offset = 0;

  files.forEach(file => {
    const nameBytes = encoder.encode(file.path);
    const crc = crc32(file.bytes);
    const size = file.bytes.length;

    const local = new Uint8Array(30 + nameBytes.length);
    writeUint32(local, 0, 0x04034b50);
    writeUint16(local, 4, 20);
    writeUint16(local, 6, 0x0800);
    writeUint16(local, 8, 0);
    writeUint16(local, 10, 0);
    writeUint16(local, 12, 0);
    writeUint32(local, 14, crc);
    writeUint32(local, 18, size);
    writeUint32(local, 22, size);
    writeUint16(local, 26, nameBytes.length);
    writeUint16(local, 28, 0);
    local.set(nameBytes, 30);

    const central = new Uint8Array(46 + nameBytes.length);
    writeUint32(central, 0, 0x02014b50);
    writeUint16(central, 4, 20);
    writeUint16(central, 6, 20);
    writeUint16(central, 8, 0x0800);
    writeUint16(central, 10, 0);
    writeUint16(central, 12, 0);
    writeUint16(central, 14, 0);
    writeUint32(central, 16, crc);
    writeUint32(central, 20, size);
    writeUint32(central, 24, size);
    writeUint16(central, 28, nameBytes.length);
    writeUint16(central, 30, 0);
    writeUint16(central, 32, 0);
    writeUint16(central, 34, 0);
    writeUint16(central, 36, 0);
    writeUint32(central, 38, 0);
    writeUint32(central, 42, offset);
    central.set(nameBytes, 46);

    localParts.push(local, file.bytes);
    centralParts.push(central);
    offset += local.length + size;
  });

  const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0);
  const eocd = new Uint8Array(22);
  writeUint32(eocd, 0, 0x06054b50);
  writeUint16(eocd, 4, 0);
  writeUint16(eocd, 6, 0);
  writeUint16(eocd, 8, files.length);
  writeUint16(eocd, 10, files.length);
  writeUint32(eocd, 12, centralSize);
  writeUint32(eocd, 16, offset);
  writeUint16(eocd, 20, 0);

  return new Blob([...localParts, ...centralParts, eocd], {
    type: 'application/zip',
  });
}

function triggerBlobDownload(blob, filename) {
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
}

export async function downloadExplorer360ScanFolder(folder) {
  if (!folder.files || folder.files.length === 0) {
    throw new Error('No files to download');
  }

  const entries = [];
  for (let i = 0; i < folder.files.length; i += 1) {
    const file = folder.files[i];
    try {
      const blob = await downloadAwsFile(file.link);
      const bytes = new Uint8Array(await blob.arrayBuffer());
      const path = sanitizeZipPath(file.path || file.fileName);
      if (path) {
        entries.push({ path, bytes });
      }
    } catch (error) {
      console.error('Failed to download 360 scan file', file.link, error);
    }
  }

  if (entries.length === 0) {
    throw new Error('Failed to download 360 scan files');
  }

  triggerBlobDownload(
    buildStoreZip(entries),
    `${folder.folderName || '360-scan'}.zip`,
  );
}
